import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { Messages } from '../../../codegen/Localize';
import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { BrowserTitleService } from '../../../ts/services/BrowserTitleService';
import { Telemetry } from '../../../ts/services/TelemetryService';
import { enterSpace } from '../../actions/spaces/EnterSpaceAction';
import { requestSpaceContents } from '../../actions/spaces/RequestSpaceContentsActions';
import { ServiceLocator } from '../../ServiceLocator';
import { MetricsSubspaceName, SpaceInfo, SpaceItemForNonEntitySpace } from '../../state/SpaceInfo';
import { MapDispatchToProps } from '../../utils/ConnectUtils';
import { ConnectedQueryParamUpdateProvider } from '../RouterHelpers/QueryParamUpdateProvider';
import { ConnectedSpaceInfoProvider } from '../RouterHelpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { MetricsSpace } from './MetricsSpace';

export interface MetricsSpaceProviderComponentOwnProps extends ReactDirectiveProps {
  readonly activeSubspaceName: MetricsSubspaceName;
  readonly urls: SpacesSiteUrls;
}

export interface MetricsSpaceAngularProps {
  readonly browserTitleService: BrowserTitleService;
  readonly telemetry: Telemetry;
}

export interface MetricsSpaceProviderDispatchProps {
  readonly enterSpace: (spaceInfo: SpaceInfo) => void;
  readonly requestSpaceContents: () => void;
}

export type MetricsSpaceProviderComponentProps = MetricsSpaceProviderComponentOwnProps & MetricsSpaceAngularProps & MetricsSpaceProviderDispatchProps;

export const MetricsSpaceProviderComponent: React.FunctionComponent<MetricsSpaceProviderComponentProps> = (props) => {

  React.useEffect(() => {
    props.browserTitleService.setTitleWithPlaceEntityName({ placeEntityName: Messages.metricsBrowserTitle() });
  }, []);

  React.useEffect(() => {
    props.enterSpace({
      item: SpaceItemForNonEntitySpace,
      spaceType: 'metric',
      subspaceName: props.activeSubspaceName
    });
    props.requestSpaceContents();
  }, [props.activeSubspaceName]);

  return <MetricsSpace
    angularContext={props.angularContext}
    urls={props.urls}
    activeSubspaceName={props.activeSubspaceName}
    telemetry={props.telemetry}
  />;
};

const MetricsSpaceAngularHOC = angularProviderHOC<MetricsSpaceProviderProps & MetricsSpaceProviderDispatchProps, MetricsSpaceAngularProps, MetricsSpaceAngularProps>({
  getAngularServicesWithServiceLocator: () => ({
    browserTitleService: ServiceLocator.get<BrowserTitleService>('BrowserTitleService'),
    telemetry: ServiceLocator.get<Telemetry>('TelemetryService'),
  }),
  selectPropsFromAngularServices: angularServices => angularServices,
});

const AngularWrappedMetricsSpace: React.ComponentType<MetricsSpaceProviderProps & MetricsSpaceProviderDispatchProps>
  = MetricsSpaceAngularHOC(MetricsSpaceProviderComponent);

const mapDispatchToProps: MapDispatchToProps<MetricsSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  requestSpaceContents,
  enterSpace,
}, dispatch);

export const ConnectedMetricsSpaceProvider: React.ComponentClass<MetricsSpaceProviderComponentOwnProps>
  = connect(undefined, mapDispatchToProps)(AngularWrappedMetricsSpace);

export interface MetricsSpaceProviderProps extends ReactDirectiveProps {
  readonly routeLocationPath: string;
  readonly routeLocationSearch: string;
  readonly activeSubspaceName: MetricsSubspaceName;
  readonly urls: SpacesSiteUrls;
}

export const MetricsSpaceProvider: React.FunctionComponent<MetricsSpaceProviderProps> = (props) => {
  const MetricsSpaceInfo: SpaceInfo = {
    item: SpaceItemForNonEntitySpace,
    spaceType: 'metric',
    subspaceName: props.activeSubspaceName
  };
  const metricsSpaceProps = {
    urls: props.urls,
    angularContext: props.angularContext,
    activeSubspaceName: props.activeSubspaceName
  };

  return (
    <ConnectedSpaceInfoProvider spaceInfo={MetricsSpaceInfo}>
      <ConnectedQueryParamUpdateProvider
        routeLocationPath={props.routeLocationPath}
        routeLocationSearch={props.routeLocationSearch}
        shouldUpdateBreadcrumbs={false}
        shouldUpdateContentTypes={false}
        shouldUpdateFilters={true}
        shouldUpdateSort={true}
      >
        <ConnectedMetricsSpaceProvider {...metricsSpaceProps} />
      </ConnectedQueryParamUpdateProvider>
    </ConnectedSpaceInfoProvider>
  );
};
